// src/components/home/HomeSearch.js
import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useSearch from "../../hooks/useSearch";
import LoadingSpinner from '../shared/LoadingSpinner';
import './HomeSearch.css';

// Placeholder image for fallbacks
const PLACEHOLDER_IMAGE = process.env.REACT_APP_PLACEHOLDER_IMAGE || 'https://via.placeholder.com/50';

const HomeSearch = () => {
  const navigate = useNavigate();
  const searchContainerRef = useRef(null);
  
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    results: liveSearchResults,
    isSearching: isLiveSearching,
    hasTyped,
    hasResults,
    showResults,
    clearResults
  } = useSearch();

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        clearResults();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [clearResults]);

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
  };

  // Function to handle clicking a search result
  const handleResultClick = (item, type) => {
    if (!item) return;
    
    // Get ID with fallbacks
    const id = item.id || item.id || item.mal_id;
    if (!id) {
      console.error(`Cannot navigate: Missing ID for ${type} item`, item);
      return;
    }
    
    if (type === 'anime') {
      navigate(`/anime/${id}`);
    } else if (type === 'manga') {
      navigate(`/manga/${id}`);
    } else if (type === 'character') {
      navigate(`/character/${id}`);
    }

    setSearchQuery('');
    clearResults();
  };

  // Handle input focus
  const handleInputFocus = () => {
    // Don't show results just because the input received focus
    // Results will only show after the user types something
    if (!searchQuery.trim()) {
      clearResults();
    }
  };

  return (
    <section className="search-section">
      <form onSubmit={handleSearch} className="search-form">
        <div className="search-input-container" ref={searchContainerRef}>
          <input
            type="text"
            placeholder="Search for anime, manga, or characters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleInputFocus}
            className="search-input"
          />
          {isLiveSearching && (
            <div className="live-search-loading">
              {/* Replace the existing loading icon with the LoadingSpinner component */}
              <div className="search-spinner">
                <div className="spinner-mini"></div>
              </div>
            </div>
          )}
          {showResults && (
            <div className="live-search-dropdown">
              {/* Anime Results */}
              {liveSearchResults.anime.length > 0 && (
                <>
                  <div className="search-category">
                    <span className="category-icon anime-icon">🎬</span>
                    Anime
                  </div>
                  {liveSearchResults.anime.map((item, index) => (
                    <div
                      key={`anime-${item.id || item.mal_id || index}`}
                      className="search-result-item"
                      onClick={() => handleResultClick(item, 'anime')}
                    >
                      <div className="search-result-thumbnail">
                        <img 
                          src={item.images?.jpg?.small_image_url || PLACEHOLDER_IMAGE} 
                          alt={item.title} 
                          onError={(e) => {
                            e.target.src = PLACEHOLDER_IMAGE;
                          }}
                        />
                      </div>
                      <div className="search-result-details">
                        <div className="search-result-title">{item.titleEnglish || item.title}</div>
                        <div className="search-result-type">
                          <span className="result-type-badge" 
                                style={{ background: 'var(--anime-accent-primary)' }}>
                            {item.type || 'Anime'}
                          </span>
                          {item.score && <span className="result-score">★ {item.score}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Manga Results */}
              {liveSearchResults.manga.length > 0 && (
                <>
                  <div className="search-category">
                    <span className="category-icon manga-icon">📚</span>
                    Manga
                  </div>
                  {liveSearchResults.manga.map((item, index) => (
                    <div
                      key={`manga-${item.id || item.mal_id || index}`}
                      className="search-result-item"
                      onClick={() => handleResultClick(item, 'manga')}
                    >
                      <div className="search-result-thumbnail">
                        <img 
                          src={item.images?.jpg?.small_image_url || PLACEHOLDER_IMAGE} 
                          alt={item.title} 
                          onError={(e) => {
                            e.target.src = PLACEHOLDER_IMAGE;
                          }}
                        />
                      </div>
                      <div className="search-result-details">
                        <div className="search-result-title">{item.titleEnglish || item.title}</div>
                        <div className="search-result-type">
                          <span className="result-type-badge"
                                style={{ background: 'var(--manga-accent-primary)' }}>
                            {item.type || 'Manga'}
                          </span>
                          {item.score && <span className="result-score">★ {item.score}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Character Results */}
              {liveSearchResults.characters.length > 0 && (
                <>
                  <div className="search-category">
                    <span className="category-icon character-icon">👤</span>
                    Characters
                  </div>
                  {liveSearchResults.characters.map((item, index) => (
                    <div
                      key={`character-${item.id || item.mal_id || index}`}
                      className="search-result-item"
                      onClick={() => handleResultClick(item, 'character')}
                    >
                      <div className="search-result-thumbnail">
                        <img 
                          src={item.images?.webp?.image_url || item.images?.jpg?.image_url || PLACEHOLDER_IMAGE} 
                          alt={item.name} 
                          onError={(e) => {
                            e.target.src = PLACEHOLDER_IMAGE;
                          }}
                        />
                      </div>
                      <div className="search-result-details">
                        <div className="search-result-title">{item.name}</div>
                        <div className="search-result-type">
                          <span className="result-type-badge character-badge">
                            Character
                          </span>
                          {item.favorites && <span className="result-favorites">❤ {item.favorites}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {/* Loading state when searching but no results yet */}
              {isLiveSearching && !hasResults && searchQuery.trim() && (
                <div className="search-loading-state">
                  <LoadingSpinner />
                </div>
              )}
              
              {/* No results message */}
              {!isLiveSearching && !hasResults && hasTyped && searchQuery.trim() && (
                <div className="no-results-message">
                  <p>No results found for "{searchQuery}"</p>
                </div>
              )}
              
              {/* View All Results Button */}
              {showResults && searchQuery.trim() && hasResults && (
                <div className="view-all-results">
                  <button 
                    onClick={handleSearch}
                    className="view-all-button"
                  >
                    View All Results for "{searchQuery}"
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <button type="submit" className="search-button">
          Search
        </button>
      </form>
    </section>
  );
};

export default HomeSearch;